import { useHasPermission, useHasAnyPermission } from "@providers/auth";
import { useAppContext } from "@providers/app/app.context";
import { Box, CircularProgress } from "@mui/material";
import { FunctionComponent, PropsWithChildren, ReactNode } from "react";
import { FeatureDenied } from "./Views/FeatureDenied";
import { PermissionDenied } from "./Views/PermissionDenied";

const DefaultScopeLoading: FunctionComponent = () => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      minHeight: "40vh",
    }}
  >
    <CircularProgress size={32} />
  </Box>
);

export const ScopeGuard: FunctionComponent<PropsWithChildren<{
  scope?: string;
  ErrorComponent?: ReactNode;
  LoadingComponent?: ReactNode;
}>> = ({ children, scope, ErrorComponent, LoadingComponent }) => {
  const hasPermission = useHasPermission(scope || "");
  if (!scope || hasPermission) {
    return <>{children}</>;
  }
  if (hasPermission === false && scope) {
    return <>{ErrorComponent}</>;
  }
  return <>{LoadingComponent ?? <DefaultScopeLoading />}</>;
};
export const ViewScopeGuard: FunctionComponent<PropsWithChildren> = ({ children }) => {
  const app = useAppContext();
  return (
    <ScopeGuard scope={app.viewScope} ErrorComponent={<PermissionDenied />}>
      {children}
    </ScopeGuard>
  );
};

export const FeatureScopeGuard: FunctionComponent<PropsWithChildren<{ scope: string }>> = ({
  scope,
  children,
}) => {
  return (
    <ScopeGuard scope={scope} ErrorComponent={<FeatureDenied />}>
      {children}
    </ScopeGuard>
  );
};

/** User must have at least one scope (OR). Same loading / denied UX as {@link ScopeGuard}. */
export const AnyScopeGuard: FunctionComponent<
  PropsWithChildren<{ scopes: string[] }>
> = ({ scopes, children }) => {
  const allowed = useHasAnyPermission(scopes);
  if (allowed === undefined) {
    return <DefaultScopeLoading />;
  }
  if (allowed) {
    return <>{children}</>;
  }
  return <PermissionDenied />;
};
